import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { api } from "@/lib/api-client";
import { getPostcodeFromLocationName } from "@repo/countries";
import type { DetailedAgent } from "@repo/database";

const LIMIT = 6;

type RelaxationTier =
  | "exactFilters"
  | "dropSpecialties"
  | "dropLanguages"
  | "locationOnly";

/**
 * Progressively relaxes broker search filters to find "near-miss" results
 * when the exact filter combination returns zero.
 *
 * Tiers (tried in order, stops at first with results):
 *  1. Exact filters — languages, specialties, name, location
 *  2. Drop specialties — keep languages, name, location
 *  3. Drop languages — keep name, location
 *  4. Location only — drop all non-location filters
 */
export function useRelaxedBrokers(opts: {
  enabled: boolean;
  countryCode?: string;
}): {
  brokers: DetailedAgent[];
  tier: RelaxationTier | null;
  isLoading: boolean;
} {
  const filters = useAtomValue(agentsFiltersAtom);

  const locationFields = useMemo(
    () => ({
      countryCode: opts.countryCode,
      neighborhood: filters.location?.context.neighborhood?.name,
      city: filters.location?.context.place?.name,
      county: filters.location?.context.district?.name,
      state: filters.location?.context.region?.name,
      zipCode: getPostcodeFromLocationName(
        opts.countryCode ?? "",
        filters.location?.name,
      ),
    }),
    [opts.countryCode, filters.location],
  );

  const shared = {
    ...locationFields,
    sort: "recommended" as const,
    limit: LIMIT,
  };

  const tier1Query = useMemo(
    () => ({
      ...shared,
      name: filters.name ?? undefined,
      languageIds: filters.languageIds.length > 0 ? filters.languageIds : undefined,
      specialtyIds: filters.specialtyIds.length > 0 ? filters.specialtyIds : undefined,
    }),
    [shared, filters.name, filters.languageIds, filters.specialtyIds],
  );

  const tier2Query = useMemo(
    () => ({
      ...shared,
      name: filters.name ?? undefined,
      languageIds: filters.languageIds.length > 0 ? filters.languageIds : undefined,
    }),
    [shared, filters.name, filters.languageIds],
  );

  const tier3Query = useMemo(
    () => ({
      ...shared,
      name: filters.name ?? undefined,
    }),
    [shared, filters.name],
  );

  const tier4Query = useMemo(() => ({ ...shared }), [shared]);

  const tier1 = api.agents.get.useQuery(tier1Query, {
    enabled: opts.enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier1Empty = tier1.isSuccess && tier1.data.agents.filter((a) => a.isVerified).length === 0;

  const tier2 = api.agents.get.useQuery(tier2Query, {
    enabled: opts.enabled && tier1Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier2Empty = tier2.isSuccess && tier2.data.agents.filter((a) => a.isVerified).length === 0;

  const tier3 = api.agents.get.useQuery(tier3Query, {
    enabled: opts.enabled && tier1Empty && tier2Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier3Empty = tier3.isSuccess && tier3.data.agents.filter((a) => a.isVerified).length === 0;

  const tier4 = api.agents.get.useQuery(tier4Query, {
    enabled: opts.enabled && tier1Empty && tier2Empty && tier3Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const result = useMemo<{
    brokers: DetailedAgent[];
    tier: RelaxationTier | null;
  }>(() => {
    const t1 = tier1.data?.agents.filter((a) => a.isVerified) ?? [];
    if (t1.length > 0) return { brokers: t1.slice(0, LIMIT), tier: "exactFilters" };

    const t2 = tier2.data?.agents.filter((a) => a.isVerified) ?? [];
    if (t2.length > 0) return { brokers: t2.slice(0, LIMIT), tier: "dropSpecialties" };

    const t3 = tier3.data?.agents.filter((a) => a.isVerified) ?? [];
    if (t3.length > 0) return { brokers: t3.slice(0, LIMIT), tier: "dropLanguages" };

    const t4 = tier4.data?.agents.filter((a) => a.isVerified) ?? [];
    if (t4.length > 0) return { brokers: t4.slice(0, LIMIT), tier: "locationOnly" };

    return { brokers: [], tier: null };
  }, [tier1.data, tier2.data, tier3.data, tier4.data]);

  const isLoading =
    (opts.enabled && tier1.isLoading) ||
    (tier1Empty && tier2.isLoading) ||
    (tier1Empty && tier2Empty && tier3.isLoading) ||
    (tier1Empty && tier2Empty && tier3Empty && tier4.isLoading);

  return { ...result, isLoading };
}
